import Input from "@mui/material/Input";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@mui/styles";
import Box from "@mui/material/Box";
import {
  TextField,
  Autocomplete,
  Checkbox,
  Popper,
  Paper,
} from "@mui/material";
import clsx from "clsx";
import { useMemo, useState, useEffect } from "react";
import { useAsyncDebounce } from "react-table";
import IconButton from "@mui/material/IconButton";
import Autorenew from "@mui/icons-material/Autorenew";
import { setResetFilters } from "../e-commerce/store/ordersSlice";
import { withoutKey } from 'app/main/apps/common/optionProps';
import { splitFilterValues } from './filterValues';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      maxWidth: 300,
    },
  },
};

const useStyles = makeStyles({
  select: {
    '&:after': {
      border: 'none',
    },
    '& .MuiSelect-select': {
      padding: '4px 0 5px',
    },

    '& .MuiOutlinedInput-notchedOutline': {
      borderLeft: 'none',
      borderRight: 'none',
      borderTop: 'none',
      borderRadius: 0,
      borderBottom: '2px solid #06b6d4',
    },
  },
  selectRoot: {
    '& .MuiFormControl-root': {
      margin: '0',
    },
  },
  input: {
    '&:before': {
      borderBottom: '2px solid #06b6d4',
    },
    '&:after': {
      borderBottom: '2px solid #06b6d4',
    },
  },
});

const TextFilter = ({ column, params, updateParams }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const resetFilter = useSelector(({ eCommerceApp }) => eCommerceApp.orders.resetFilter);
  const { filterValue, setFilter, paramKey } = column;
  let value = filterValue;
  if (paramKey) value = params[paramKey]

  // Blanking the displayed value stays in render: it is a pure local computation, not a state
  // update, and it keeps the input from showing a stale value for the frame between the reset
  // and the cleared state arriving back through `filterValue` / `params`.
  if (resetFilter) value = ''

  /**
   * PGSS-5147: clear on Reset Filters from an effect, not from the render body.
   *
   * This block used to run inline during render, calling setFilter (which updates AuraTable's
   * state) and dispatching from inside another component's render pass. React 19 does not
   * support that, and it made the reset apply unevenly:
   *
   *  - the FIRST TextFilter to render flipped the flag false, so every sibling TextFilter
   *    rendered after it saw `false` and never cleared — which columns cleared depended on
   *    render order;
   *  - `EntitiesContent` clears the request params off the SAME flag, from an effect. Effects
   *    run after render, so the render-phase dispatch could beat it to the flag and leave the
   *    inputs blank while the API params, and therefore the list, stayed filtered.
   *
   * In an effect every TextFilter mounted in this commit reads the same `true` it rendered
   * with, so they all clear, and the container's effect still sees the flag. The dispatch is
   * left here rather than given solely to the container: screens using TextFilter without one
   * of the *Content containers have no other owner for the flag, and it would stick on.
   */
  useEffect(() => {
    if (!resetFilter) {
      return;
    }
    setFilter('')
    dispatch(setResetFilters(false))
  }, [resetFilter, setFilter, dispatch])

  return (
    <div>
      <Input
        className={clsx(classes.input, 'm-w-100')}
        placeholder="Search"
        value={value || ''}
        onClick={(ev) => ev.stopPropagation()}
        onChange={(ev) => {
          ev.stopPropagation();
          if (paramKey)
            updateParams(paramKey, ev.target.value)
          else
            setFilter(ev.target.value);
        }}
      />
    </div>
  );
};

export const NumberFilter = ({ column, params, updateParams }) => {
  const classes = useStyles();
  const { filterValue, setFilter, paramKey } = column;
  let value = filterValue;
  if (paramKey) value = params[paramKey]

  return (
    <div>
      <Input
        type="number"
        onWheel={(e) => e.target.blur()}
        className={clsx(classes.input, 'm-w-100 number-no-scroll')}
        placeholder="Search"
        value={value || ''}
        onClick={(ev) => ev.stopPropagation()}
        onChange={(ev) => {
          ev.stopPropagation();
          if (paramKey)
            updateParams(paramKey, ev.target.value)
          else
            setFilter(ev.target.value);
        }}
      />
    </div>
  );
};

export const DateFilter = ({ column, params, updateParams }) => {
  const { filterValue, setFilter, paramKey } = column;
  let value = filterValue;
  if (paramKey) value = params[paramKey]

  return (
    <div>
      <Input
        type="date"
        className='w-[110px]'
        placeholder=""
        value={value || ''}
        onClick={(ev) => ev.stopPropagation()}
        onChange={(ev) => {
          ev.stopPropagation();
          if (paramKey)
            updateParams(paramKey, ev.target.value)
          else
            setFilter(ev.target.value);
        }}
      />
    </div>
  );
};

export function SelectFilter(props) {
  const { column, menuData, setSelectedValue, params, updateParams, multiple } = props
  const [open, setOpen] = useState(false)
  const classes = useStyles();
  const { filterValue, setFilter, id, filter, paramKey, headerSxStyle = {}, Header } = column;
  let value = filterValue;
  if (paramKey) value = params[paramKey]
  if (multiple && value && !Array.isArray(value)) value = splitFilterValues(value)

  const onChange = (ev, newValue) => {
    let targetValue = ev.target.value
    ev.stopPropagation();

    if (multiple && newValue.props.value === '') {
      targetValue = ['']
    }

    if (multiple && newValue.props.value && value) {
      value = value.filter(v => v)
      targetValue = targetValue.filter(v => v)
    }
    if (paramKey)
      updateParams(paramKey, targetValue)
    else if (filter === 'equals' && targetValue === '') {
      setFilter(null);
      if (setSelectedValue) {
        setSelectedValue(null);
      }
    } else {
      setFilter(targetValue);
      if (setSelectedValue) {
        setSelectedValue(ev);
      }
    }
    if (Header === "Shape" && (id === "type_name" || id === "type.name")) {
      // Comes here when type drop down changes
      // Update sub type and polish drop down data and params
      const { columns } = props;
      const polishColumn = columns.find((d) => d.id === "polish");
      if (polishColumn && polishColumn.paramKey) {
        const polishParamPresent = params[polishColumn.paramKey];
        if (polishParamPresent) {
          // Update only if polish filter data is already selected
          updateParams(polishColumn.paramKey, "");
          polishColumn.setFilter(null);
          if (setSelectedValue) {
            setSelectedValue(null);
          }
        }
      }
      const subTypeColumn = columns.find((d) => d.id === "sub_type_name");
      if (subTypeColumn && subTypeColumn.paramKey) {
        const subTypeParamPresent = params[subTypeColumn.paramKey];
        if (subTypeParamPresent) {
          // Update only if sub Type filter data is already selected
          updateParams(subTypeColumn.paramKey, "");
          subTypeColumn.setFilter(null);
          if (setSelectedValue) {
            setSelectedValue(null);
          }
        }
      }
      props.typeChanged(targetValue);
    }
  }

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const isEmpty = (selected) => {
    if (multiple)
      return (selected.length < 2 && selected[0] === '') || !selected.length
    return selected === ''
  }

  return (
    <div className={classes.selectRoot}>
      <FormControl sx={{ m: 1, width: '100%' }}>
        <Select
          id={`filter-${id}}`}
          size="small"
          displayEmpty
          value={multiple ? value || [] : value || ''}
          onClick={(ev) => ev.stopPropagation()}
          onClose={handleClose}
          onOpen={handleOpen}
          multiple={multiple}
          open={multiple ? open : undefined}
          onChange={onChange}
          renderValue={(selected) => {
            if (isEmpty(selected)) {
              return <Box sx={{ color: '#8e8e8e' }}>All</Box>;
            }

            return (
              <Box
                style={{
                  textTransform: 'capitalize',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  "flex-wrap": "nowrap",
                  width: (multiple && headerSxStyle?.width) ? headerSxStyle?.width : 'initial'
                }}
              >
                {multiple ? selected.join(', ') : selected}
              </Box>
            );
          }}
          MenuProps={MenuProps}
          inputProps={{ 'aria-label': 'Without label' }}
          className={classes.select}
        >
          <MenuItem value="" onClick={(ev) => {
            ev.stopPropagation();
            setOpen(false)
          }}>
            <em>All</em>
          </MenuItem>
          {menuData &&
            menuData?.map((name) => (
              <MenuItem key={name} value={name}>
                {multiple && <Checkbox size='small' className='p-0 pr-4' checked={value?.indexOf(name) > -1} />}
                <div style={{ textTransform: 'capitalize' }}>{name}</div>
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    </div >
  );
}

export const GlobalFilter = ({ filter, setFilter }) => {
  const [value, setValue] = useState(filter);

  const onChange = useAsyncDebounce((valueP) => {
    setFilter(valueP || undefined);
  }, 1000);

  return (
    <span>
      Search:{' '}
      <input
        value={value || ''}
        onChange={(e) => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
      />
    </span>
  );
};

export const ResetFilter = (props) => {
  return (
    <IconButton
      color="secondary"
      aria-haspopup="true"
      onClick={props.handleResetFiltersClick}
      size="large"
    >
      <Autorenew />
    </IconButton>
  );
};

/* Custom Popper that forces the dropdown Paper size */
function DropdownPopper(props) {
  const { children, style, ...other } = props;
  // style contains positioning from Autocomplete; preserve it
  return (
    <Popper {...other} style={{ ...style, zIndex: 1500 }}>
      <Paper
        sx={{
          width: 280, // Dropdown width
          maxHeight: 500, // Dropdown max height
          overflow: "auto",
        }}
      >
        {children}
      </Paper>
    </Popper>
  );
}

export function AutocompleteFilter(filterProps) {
  const {
    column,
    menuData,
    multiple,
    setSelectedValue,
    params: globalParams,
    updateParams: updateGlobalParams,
  } = filterProps;

  const {
    filterValue,
    setFilter,
    id,
    filter,
    paramKey,
    headerSxStyle = {},
  } = column;

  /* -------------------- Resolve current value -------------------- */
  let resolvedValue = paramKey ? globalParams[paramKey] : filterValue;

  if (multiple) {
    if (!resolvedValue) {
      resolvedValue = [];
    } else if (!Array.isArray(resolvedValue)) {
      resolvedValue = splitFilterValues(resolvedValue);
    }
  } else if (!resolvedValue) {
    resolvedValue = "";
  }

  /* -------------------- Display value -------------------- */
  const getDisplayValue = () => {
    if (multiple) return resolvedValue.length ? resolvedValue : [];
    return resolvedValue || "";
  };

  /* -------------------- Dropdown search input -------------------- */
  const [inputValue, setInputValue] = useState(resolvedValue || "");

  // Sync inputValue with resolvedValue changes
  useEffect(() => {
    setInputValue(resolvedValue || "");
  }, [resolvedValue]);

  /* -------------------- Handle Change -------------------- */
  const handleChange = (event, selected, reason) => {
    // prevent updates for inside the autocomplete's search field
    if (reason === "input") return;

    // prevent API call when clear icon clicked but no actual filter is set
    if (reason === "clear" && event?.type === "click") {
      const noActiveFilter = multiple
        ? resolvedValue.length === 0
        : resolvedValue === "" || resolvedValue === null;

      if (noActiveFilter) return;
    }

    let target = selected;

    if (multiple) {
      if (Array.isArray(selected)) {
        target = selected;
      }
    } else {
      target = selected || "";
    }

    // safe update only after genuine user action
    if (paramKey) {
      updateGlobalParams(paramKey, multiple ? target.join(",") : target);
    } else if (filter === "equals" && target === "") {
      setFilter(null);
      if (setSelectedValue) setSelectedValue(null);
    } else {
      setFilter(target);
      if (setSelectedValue) setSelectedValue(selected);
    }
  };

  /* -------------------- Options -------------------- */
  // Prevents re-creating the array on every render
  const options = useMemo(() => [...menuData], [menuData]);

  /* -------------------- Label -------------------- */
  const getLabel = (option) => {
    if (typeof option === "string") return option;
    return option;
  };

  /* -------------------- Render Option -------------------- */
  const renderOption = (optionProps, option, { selected }) => (
    <li {...withoutKey(optionProps)} key={option}>
      {multiple && option !== "ALL" && (
        <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
      )}
      <span className="capitalize">{getLabel(option)}</span>
    </li>
  );

  return (
    <FormControl sx={{ m: 1, width: "100%", display: "block", margin: 0 }}>
      <Autocomplete
        id={`filter-${id}`}
        size="small"
        multiple={multiple}
        freeSolo
        disableClearable={false}
        PopperComponent={DropdownPopper}
        options={options}
        value={getDisplayValue()}
        onChange={handleChange}
        //  controlled search input value
        inputValue={inputValue}
        onInputChange={(event, newValue, reason) => {
          if (reason === "clear") {
            setInputValue(""); // allow clearing
          } else {
            setInputValue(newValue); // preserve typed value
          }
        }}
        renderOption={renderOption}
        getOptionLabel={getLabel}
        onClick={(ev) => ev.stopPropagation()}
        sx={{
          width: headerSxStyle.width || "100%",
          display: "block",
          margin: 0,
        }}
        renderInput={(inputProps) => (
          <TextField
            {...inputProps}
            variant="standard"
            placeholder="All"
            InputProps={inputProps.InputProps}
          />
        )}
        selectOnFocus
        clearOnBlur={false}
        handleHomeEndKeys
        filterOptions={(opts, state) => {
          const search = inputValue?.toLowerCase().trim();
          if (!search) return opts;

          // separate matched + unmatched
          const matched = [];
          const unmatched = [];

          opts.forEach((opt) => {
            const label = String(opt).toLowerCase();
            if (label.includes(search)) {
              matched.push(opt);
            } else {
              unmatched.push(opt);
            }
          });

          // merged result keeps ALL options
          return [...matched, ...unmatched];
        }}
      />
    </FormControl>
  );
}

export default TextFilter;
